/* My jobs (the pack's word; "My tasks" confused people — owner, 2026-10-08): what needs me,
 * as a list, by deadline, on a calendar or a plan.
 *
 * The "what needs me" home (UX bar): the counters at the top are the three
 * questions people open it with — is anything waiting on me, is anything
 * late, has anybody said something — and each one is also the filter that
 * answers it. Create buttons show only for the kinds this person may make,
 * named in the pack's own words.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  EmptyByFilter, EmptyFirstTime, Failed, Loading, Offline, ScreenState, TooMuchData, useToast,
} from '@jackdesk/shell'
import type { ErpError } from '../api'
import { CalendarView } from '../BoardView'
import { useErp } from '../context'
import { formatDate } from '../format'
import { GanttView } from '../GanttView'
import type { Doc, DocField, DocMeta } from '../types'
import type { Kind, PacksActive, WorkCard, WorkList, WorkRow, WorkScope, Who } from './types'
import { usePersonName } from './Pickers'
import { WorkTable } from './WorkTable'
import { StatCards } from './StatCards'
import { clock, useTick } from './Timer'
import type { RunningTimer } from './types'
import { WorkTabs } from './Projects'
import { usePacksActive } from './usePacks'
import {
  BUCKETS, bucketDate, bucketOf, findKind, hasReviewer, homeLabel, matchesSearch, priorityOf, roleWord,
  stateLabel, stateTone, whoWord,
} from './words'
import type { Bucket } from './words'

type View = 'list' | 'deadline' | 'calendar' | 'gantt'
type Scope = WorkScope
type Counter = 'needs_me' | 'overdue' | 'comments' | null

const PREFS = 'erp.work.prefs'

function readPrefs(): { view: View; scope: Scope } {
  try {
    const raw = localStorage.getItem(PREFS)
    if (raw) return { view: 'list', scope: 'mine', ...(JSON.parse(raw) as object) }
  } catch { /* a private window starts with the defaults */ }
  return { view: 'list', scope: 'mine' }
}

function savePrefs(p: { view: View; scope: Scope }) {
  try { localStorage.setItem(PREFS, JSON.stringify(p)) } catch { /* nothing depends on it */ }
}

export function WorkHome({ onOpen, onMake, onSetUp, onReports, onProjects, mayManage }: {
  onOpen: (name: string) => void
  onMake: (pack: string, kind: string) => void
  /** The projects screen, when the app has one. */
  onProjects?: () => void
  /** The packs' reports, when the app has a screen for them. */
  onReports?: () => void
  /** Where an owner switches a pack on, when none is. */
  onSetUp?: () => void
  mayManage?: boolean
}) {
  const erp = useErp()
  const toast = useToast()
  const nameOf = usePersonName()
  const { active, failed: packsFailed, reload: reloadPacks } = usePacksActive()
  const [prefs, setPrefs] = useState(readPrefs)
  // The search box (owner, 2026-10-09): over what is loaded, which is this organisation's own.
  const [q, setQ] = useState('')
  const [closed, setClosed] = useState(false)
  const [counter, setCounter] = useState<Counter>(null)
  // A card picked: the list is what it counted, asked of the gateway with its filters (owner, 2026-10-11).
  const [card, setCard] = useState<WorkCard | null>(null)
  const cardKey = card ? JSON.stringify(card.filters) : ''
  const [as, setAs] = useState<Who | ''>('')
  const [data, setData] = useState<WorkList | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [stale, setStale] = useState(false)
  // My running timer, wherever it is, so it is never forgotten (2026-10-09).
  const [timer, setTimer] = useState<RunningTimer | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    erp.myTimer(controller.signal).then((r) => setTimer(r.running)).catch(() => setTimer(null))
    return () => controller.abort()
  }, [erp, data])
  const now = useTick(Boolean(timer))

  const update = (next: Partial<typeof prefs>) => {
    const merged = { ...prefs, ...next }
    setPrefs(merged); savePrefs(merged)
  }

  const load = useCallback((signal?: AbortSignal) => {
    setFailed(null)
    erp.workMine(prefs.scope, closed, signal, card?.filters)
      .then((d) => { setData(d); setStale(false) })
      .catch((e) => {
        if ((e as Error).name === 'AbortError') return
        // What was on screen stays, marked as old, rather than vanishing.
        if (data) setStale(true)
        else setFailed(e as ErpError)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erp, prefs.scope, closed, cardKey])

  useEffect(() => {
    if (!active?.packs.length) return
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load, active])

  const rows = useMemo(() => {
    let out = data?.rows ?? []
    if (counter === 'needs_me') out = out.filter((r) => r.needs_me)
    if (counter === 'overdue') out = out.filter((r) => r.overdue)
    if (counter === 'comments') out = out.filter((r) => r.unseen_comments)
    if (as) out = out.filter((r) => holds(r, as, data?.user ?? ''))
    if (q.trim()) {
      out = out.filter((r) => matchesSearch(r, q, [
        ...r.assigned.map(nameOf),
        ...[r.jd_checker, r.jd_reviewer, r.jd_approver].filter(Boolean).map((e) => nameOf(e as string))]))
    }
    return out
  }, [data, counter, as, q, nameOf])

  /** Whether this person may move a row's dates (the gateway decides again): who may make
   *  that kind of work, who made it, or who signs it off. */
  const mayMove = useCallback((r: WorkRow) => {
    const kind = findKind(active, r.jd_kind)?.kind
    const me = data?.user ?? ''
    return Boolean(kind?.may_create) || (Boolean(me) && (r.owner === me || r.jd_approver === me))
  }, [active, data])

  /** Move a row's dates and show the list as it now is; a refusal is said and thrown back so
   *  the view puts the item where it was. */
  const move = useCallback(async (name: string, body: { exp_start_date?: string | null; exp_end_date?: string | null }) => {
    try {
      const done = await erp.moveWork(name, body)
      // What else moved to make room is said, never done silently.
      const also = [
        done.moved_rows ? `its ${done.moved_rows} open row${done.moved_rows === 1 ? '' : 's'} with it` : '',
        done.job_moved ? `its job now ends ${formatDate(done.job_moved)}` : '',
        done.project_moved ? 'the project’s dates widened to fit' : '',
      ].filter(Boolean)
      toast('ok', also.length ? `Moved; ${also.join('; ')}.` : 'Moved.')
      load()
    } catch (e) {
      toast('error', (e as ErpError).message)
      throw e
    }
  }, [erp, toast, load])

  if (packsFailed) {
    return <div className="page"><Failed message={packsFailed.message} onRetry={reloadPacks}
                                         requestId={packsFailed.requestId} /></div>
  }
  if (!active) return <div className="page"><Loading rows={6} label="Opening your work" /></div>
  if (!active.packs.length) {
    return (
      <div className="page">
        <ScreenState
          title="No industry pack is switched on"
          detail={mayManage
            ? 'A pack adds the kinds of work your business runs on: jobs, requests, approvals. Switch one on to start.'
            : 'Your organisation has not switched on a pack yet. The account owner can, in Set up → Industry packs.'}
          actions={mayManage && onSetUp
            ? <button className="btn btn-primary" onClick={onSetUp}>See the packs</button> : undefined}
        />
      </div>
    )
  }

  const makeable = active.packs.flatMap((p) => p.kinds.filter((k) => k.may_create && !k.parent)
    .map((k) => ({ pack: p, kind: k })))
  const first = makeable[0]
  const words = mergedWords(active)
  const offline = failed?.code === 'offline'
  const title = homeLabel(active)
  const reviewing = active.packs.some((p) => p.kinds.some((k) => hasReviewer(k)))

  return (
    <div className="page" style={{ display: 'grid', gap: 14 }}>
      <WorkTabs on="jobs" onJobs={() => undefined} onProjects={onProjects} jobsLabel={title} />
      <div className="row row-between" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1>{title}</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            {active.packs.map((p) => p.title).join(' · ')}
          </p>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {onReports ? <button className="btn btn-quiet" onClick={onReports}>Reports</button> : null}
          {makeable.map(({ pack, kind }) => (
            <button key={kind.value}
                    className={`btn ${kind.group ? 'btn-primary' : ''}`}
                    onClick={() => onMake(pack.id, kind.key)}>
              New {kind.label.toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      <StatCards active={active} scope={prefs.scope} picked={card} onPick={setCard} onOpen={onOpen} refresh={data} />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }} role="group" aria-label="Show only">
        {card ? (
          <button className="chip chip-info" style={{ cursor: 'pointer', minHeight: 32, padding: '0 12px' }}
                  onClick={() => setCard(null)} aria-label={`Showing ${card.label}: show everything`}>
            Showing {card.label} ✕
          </button>
        ) : null}
        <CounterChip label="Needs me" count={data?.counters.needs_me} on={counter === 'needs_me'}
                     tone="info" onClick={() => setCounter(counter === 'needs_me' ? null : 'needs_me')} />
        <CounterChip label="Overdue" count={data?.counters.overdue} on={counter === 'overdue'}
                     tone="danger" onClick={() => setCounter(counter === 'overdue' ? null : 'overdue')} />
        <CounterChip label="New comments" count={data?.counters.comments} on={counter === 'comments'}
                     tone="warn" onClick={() => setCounter(counter === 'comments' ? null : 'comments')} />
        {stale ? <span className="chip chip-warn">Showing what was last loaded</span> : null}
        {timer ? (
          <button className="chip chip-danger" style={{ cursor: 'pointer', minHeight: 32, padding: '0 12px' }}
                  onClick={() => onOpen(timer.task)}>
            ● Timer running on {data?.rows.find((r) => r.name === timer.task)?.subject ?? timer.task} · {clock(timer.started_at, now)}
          </button>
        ) : null}
      </div>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Segmented label="Whose" value={prefs.scope}
                   options={[['mine', 'Mine'], ['created', 'Made by me'], ['team', 'My team'], ['all', 'Everything']]}
                   onChange={(v) => update({ scope: v as Scope })} />
        <select className="select" aria-label="As" value={as} style={{ maxWidth: 200 }}
                onChange={(e) => setAs(e.target.value as Who | '')}>
          <option value="">In any role</option>
          <option value="maker">As {roleWord(whoWord('maker', words))}</option>
          <option value="checker">As {roleWord(whoWord('checker', words))}</option>
          {reviewing ? <option value="reviewer">As {roleWord(whoWord('reviewer', words))}</option> : null}
          <option value="approver">As {roleWord(whoWord('approver', words))}</option>
        </select>
        <label className="check small">
          <input type="checkbox" checked={closed} onChange={(e) => setClosed(e.target.checked)} />
          Show finished
        </label>
        <input className="input" type="search" placeholder="Search title, project, client, person…"
               aria-label="Search the list" value={q} onChange={(e) => setQ(e.target.value)}
               style={{ maxWidth: 260, minHeight: 32 }} />
        <span className="grow" />
        <Segmented label="View" value={prefs.view}
                   options={[['list', 'List'], ['deadline', 'Deadline'], ['calendar', 'Calendar'],
                             ['gantt', 'Plan']]}
                   onChange={(v) => update({ view: v as View })} />
      </div>

      {offline && !data ? <Offline /> : null}
      {failed && !offline ? (
        <Failed message={failed.message} requestId={failed.requestId} onRetry={() => load()} />
      ) : null}
      {!failed && !data ? <Loading rows={6} label="Reading your tasks" /> : null}
      {data && prefs.scope === 'team' && !data.team?.length ? (
        <div className="card card-pad small muted" role="status">
          {data.problem ?? 'Nobody reports to you in the organisation\'s people list, so there is no team to show. '
            + 'Who reports to whom is set in the Store, on each person (Manager).'}
        </div>
      ) : null}
      {data && !data.rows.length && !card && !(prefs.scope === 'team' && !data.team?.length) ? (
        <EmptyFirstTime what={prefs.scope === 'mine' ? 'work for you' : prefs.scope === 'team' ? 'work for your team' : 'work'}
          action={first ? (
            <button className="btn btn-primary" onClick={() => onMake(first.pack.id, first.kind.key)}>
              New {first.kind.label.toLowerCase()}
            </button>) : undefined} />
      ) : null}
      {data && ((data.rows.length && !rows.length) || (card && !data.rows.length)) ? (
        <EmptyByFilter onClear={() => { setCounter(null); setAs(''); setQ(''); setCard(null) }} />
      ) : null}
      {/* The table pages the whole list from the server; the other views draw what was loaded. */}
      {data?.truncated && prefs.view !== 'list' ? <TooMuchData count={data.rows.length} /> : null}

      {rows.length ? (
        prefs.view === 'deadline' ? <ByDeadline rows={rows} active={active} today={data?.today ?? ''} onOpen={onOpen}
                                                mayMove={mayMove} onMove={move} />
        : prefs.view === 'calendar' ? <OnCalendar rows={rows} onOpen={onOpen} mayMove={mayMove} onMove={move} />
        : prefs.view === 'gantt' ? <OnPlan rows={rows} onOpen={onOpen} mayMove={mayMove} onMove={move} />
        : <WorkTable rows={rows} active={active} onOpen={onOpen}
                     server={data?.truncated ? { scope: prefs.scope, closed, q, overdue: counter === 'overdue',
                                                 filters: card?.filters } : null} />
      ) : null}
    </div>
  )
}

/** Does this row name me in that role, whether or not it is my turn? */
function holds(r: WorkRow, as: Who, me: string): boolean {
  if (as === 'checker') return r.jd_checker === me
  if (as === 'reviewer') return r.jd_reviewer === me
  if (as === 'approver') return r.jd_approver === me
  return r.assigned.includes(me)
}

function mergedWords(active: PacksActive): Kind['words'] {
  // One pack is the usual case; with two, the first pack's words lead.
  const out: Kind['words'] = {}
  for (const p of active.packs) for (const k of p.kinds) {
    for (const [key, value] of Object.entries(k.words)) {
      const w = key as keyof Kind['words']
      if (value && !out[w]) out[w] = value
    }
  }
  return out
}

function CounterChip({ label, count, on, tone, onClick }: {
  label: string; count?: number; on: boolean; tone: 'info' | 'danger' | 'warn'; onClick: () => void
}) {
  return (
    <button className={`chip ${count ? `chip-${tone}` : ''}`} aria-pressed={on} onClick={onClick}
            style={{ cursor: 'pointer', outline: on ? '2px solid var(--accent)' : undefined,
                     minHeight: 32, padding: '0 12px' }}>
      {label} <b style={{ marginLeft: 6 }}>{count ?? '…'}</b>
    </button>
  )
}

function Segmented({ label, value, options, onChange }: {
  label: string; value: string; options: [string, string][]; onChange: (v: string) => void
}) {
  return (
    <div className="row" role="group" aria-label={label}
         style={{ gap: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
      {options.map(([v, text]) => (
        <button key={v} className={`btn btn-sm ${value === v ? 'btn-primary' : 'btn-quiet'}`}
                aria-pressed={value === v} style={{ borderRadius: 0 }} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  )
}

function rowFacts(r: WorkRow, active: PacksActive) {
  const found = findKind(active, r.jd_kind)
  const kind = found?.kind
  // A job has no steps of its own: show where its rows have got to.
  const state = kind && !kind.flow ? (r.status ?? r.workflow_state) : r.workflow_state
  return {
    kind,
    state: stateLabel(state, kind?.words, kind?.flow),
    tone: stateTone(state),
    priority: priorityOf(r, kind),
    urgent: kind?.fields.find((f) => f.mirror)?.mirror?.values[String(priorityOf(r, kind))] === 'Urgent',
    turn: r.needs_me ? `Your turn as ${roleWord(whoWord(r.needs_me, kind?.words))}` : '',
  }
}

type Move = (name: string, body: { exp_start_date?: string | null; exp_end_date?: string | null }) => Promise<void>

function ByDeadline({ rows, active, today, onOpen, mayMove, onMove }: {
  rows: WorkRow[]; active: PacksActive; today: string; onOpen: (n: string) => void
  mayMove: (r: WorkRow) => boolean; onMove: Move
}) {
  const [over, setOver] = useState<Bucket | null>(null)
  // Dropped, saving: shown in its new column at once (owner, 2026-10-09: drag and drop).
  const [placed, setPlaced] = useState<Record<string, Bucket>>({})
  useEffect(() => { setPlaced({}) }, [rows])
  async function drop(name: string, bucket: Bucket) {
    const row = rows.find((r) => r.name === name)
    const date = bucketDate(bucket, today)
    if (!row || date === undefined || bucketOf(row, today) === bucket) return
    setPlaced((p) => ({ ...p, [name]: bucket }))
    const body: { exp_start_date?: string | null; exp_end_date: string | null } = { exp_end_date: date }
    // A start after the new deadline would be refused: it comes forward with it.
    if (date && row.exp_start_date && String(row.exp_start_date).slice(0, 10) > date) body.exp_start_date = date
    try { await onMove(name, body) } catch { setPlaced((p) => { const { [name]: _gone, ...rest } = p; return rest }) }
  }
  const groups = useMemo(() => {
    const map = new Map<string, WorkRow[]>()
    for (const r of rows) {
      const b = placed[r.name] ?? bucketOf(r, today)
      map.set(b, [...(map.get(b) ?? []), r])
    }
    return map
  }, [rows, today, placed])
  return (
    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      {BUCKETS.map((b) => {
        const here = groups.get(b.key) ?? []
        return (
          <section key={b.key} className="card card-pad" aria-label={b.label}
                   onDragOver={b.key !== 'overdue' ? (e) => { e.preventDefault(); setOver(b.key) } : undefined}
                   onDragLeave={() => setOver((o) => (o === b.key ? null : o))}
                   onDrop={b.key !== 'overdue' ? (e) => {
                     e.preventDefault(); setOver(null)
                     const name = e.dataTransfer.getData('text/x-jd-name')
                     if (name) void drop(name, b.key)
                   } : undefined}
                   style={{ display: 'grid', gap: 8, alignContent: 'start', minHeight: 120,
                            outline: over === b.key ? '2px dashed var(--accent)' : undefined }}>
            <div className="row row-between">
              <strong className="small" style={b.key === 'overdue' && here.length ? { color: 'var(--danger)' } : undefined}>
                {b.label}
              </strong>
              <span className="small muted">{here.length}</span>
            </div>
            {here.map((r) => {
              const f = rowFacts(r, active)
              return (
                <button key={r.name} className="card" onClick={() => onOpen(r.name)}
                        draggable={mayMove(r)}
                        onDragStart={(e) => { e.dataTransfer.setData('text/x-jd-name', r.name); e.dataTransfer.effectAllowed = 'move' }}
                        title={mayMove(r) ? 'Drag to another column to move its deadline' : undefined}
                        style={{ textAlign: 'left', padding: 8, cursor: mayMove(r) ? 'grab' : 'pointer', display: 'grid', gap: 4 }}>
                  <span className="truncate"><b>{r.subject}</b></span>
                  <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    <span className={`chip chip-${f.tone}`}>{f.state}</span>
                    {f.priority ? <span className={`chip ${f.urgent ? 'chip-danger' : ''}`}>{f.priority}</span> : null}
                  </span>
                  {r.exp_end_date ? <span className="small muted">{formatDate(r.exp_end_date)}</span> : null}
                </button>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}

const CAL_META = { name: 'Task', title_field: 'subject', fields: [] } as unknown as DocMeta
const CAL_FIELD = { fieldname: 'exp_end_date', label: 'Deadline', fieldtype: 'Date' } as DocField

function OnCalendar({ rows, onOpen, mayMove, onMove }: {
  rows: WorkRow[]; onOpen: (n: string) => void; mayMove: (r: WorkRow) => boolean; onMove: Move
}) {
  const byName = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows])
  return <CalendarView meta={CAL_META} rows={rows as unknown as Doc[]} field={CAL_FIELD} onOpen={onOpen}
    canMove={(n) => { const r = byName.get(n); return r ? mayMove(r) : false }}
    onMove={(n, day) => {
      const r = byName.get(n)
      const start = r?.exp_start_date ? String(r.exp_start_date).slice(0, 10) : null
      // Its deadline goes to that day; a start after it comes forward with it.
      return onMove(n, start && start > day ? { exp_start_date: day, exp_end_date: day } : { exp_end_date: day })
    }} />
}

function OnPlan({ rows, onOpen, mayMove, onMove }: {
  rows: WorkRow[]; onOpen: (n: string) => void; mayMove: (r: WorkRow) => boolean; onMove: Move
}) {
  const byName = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows])
  // A task with only a deadline is drawn as a one-day bar on it, rather than
  // left off the plan.
  const planned = useMemo(() => rows.map((r) => ({
    ...r, jd_from: r.exp_start_date || r.exp_end_date, jd_to: r.exp_end_date || r.exp_start_date,
  })) as unknown as Doc[], [rows])
  return <GanttView rows={planned} onOpen={onOpen} title="Plan"
                    config={{ start: 'jd_from', end: 'jd_to', title: 'subject', gantt: true }}
                    canMove={(n) => { const r = byName.get(n); return r ? mayMove(r) : false }}
                    onMove={(n, from, to) => {
                      const r = byName.get(n)
                      // Drawn a day long from its deadline alone: it keeps having no start
                      // unless the bar was stretched into one.
                      return onMove(n, !r?.exp_start_date && from === to
                        ? { exp_end_date: to } : { exp_start_date: from, exp_end_date: to })
                    }} />
}
